import { ICart } from "../../../utils/types";

/**
 * Calculate total discount available from all sources
 * This is the SINGLE SOURCE OF TRUTH for discount calculations
 */
export const calculateTotalDiscount = (cart: ICart | null): number => {
  if (!cart) return 0;

  // Get product-level discounts (sum of all product discount amounts)
  const productDiscounts =
    cart.products?.reduce(
      (sum, product) =>
        sum + Number(product?.discountAmount ?? 0) * product.productQty,
      0
    ) ?? 0;


  // Total available discount
  return productDiscounts;
};

/**
 * Calculate the actual discount that will be applied to the order
 * This caps the discount at the order total (can't go negative)
 */
export const calculateAppliedDiscount = (
  cart: ICart | null,
  isFreeDelivery: boolean
): number => {
  if (!cart) return 0;

  // Calculate base order amount (what customer needs to pay before discount)
  const productSubtotal =
    cart.products?.reduce(
      (sum, p) => sum + Number(p.newPrice) * p.productQty,
      0
    ) ?? 0;

  const deliveryFee = isFreeDelivery ? 0 : Number(cart.delivery?.price ?? 0);

  const amountBeforeDiscount = productSubtotal + deliveryFee;

  // Get total available discount
  const totalDiscount = calculateTotalDiscount(cart);

  // Cap discount at amount due (can't make order negative)
  return Math.min(totalDiscount, Math.max(0, amountBeforeDiscount));
};

/**
 * Calculate the final amount customer needs to pay
 */
export const calculateFinalAmount = (
  cart: ICart | null,
  isFreeDelivery: boolean
): number => {
  if (!cart) return 0;

  const productSubtotal =
    cart.products?.reduce(
      (sum, p) => sum + Number(p.newPrice) * p.productQty,
      0
    ) ?? 0;

  const deliveryFee = isFreeDelivery ? 0 : Number(cart.delivery?.price ?? 0);
  const discount = calculateAppliedDiscount(cart, isFreeDelivery);

  const finalAmount = productSubtotal + deliveryFee - discount;

  return Math.max(0, finalAmount);
};

/**
 * Calculate the amount to charge from user's wallet
 * This determines what gets debited during checkout
 */
export const calculateWalletChargeAmount = (
  cart: ICart | null,
  opsConfig: any,
  hasAliExpressSupplier: boolean,
  isFreeDelivery: boolean
): number => {
  if (!cart) return 0;

  const finalAmount = calculateFinalAmount(cart, isFreeDelivery);

  // If order is free after discount, no charge
  if (finalAmount === 0) return 0;

  // Pay at pickup: no upfront charge
  if (cart.paymentMethod === "pay_at_pickup") return 0;

  // Full payment scenarios
  const isPayOnline =
    hasAliExpressSupplier ||
    cart.paymentMethod === "pay_online" ||
    cart.delivery?.isPayOnline;

  if (isPayOnline) return finalAmount;

  // For delivery orders, check payment cap
  if (cart.delivery) {
    const orderAmount =
      Number(cart.totalAmountToCollect ?? 0) -
      Number(cart.totalResellerProfit ?? 0);

    const paymentCap = opsConfig?.deliveryPaymentThreshold ?? 0;

    // Below payment cap: full payment
    if (orderAmount < paymentCap) return finalAmount;

    // Above payment cap: security deposit (or less if order is cheaper)
    const securityDeposit = opsConfig?.payableSecurityDeposit ?? 0;
    return Math.min(securityDeposit, finalAmount);
  }

  // Default: full payment
  return finalAmount;
};

// ============================================
// SECURITY DEPOSIT & PAYMENT STATUS
// ============================================

export const calculatePaymentStatus = (
  cart: ICart | null,
  opsConfig: any,
  hasAliExpressSupplier: boolean
): string => {
  if (hasAliExpressSupplier) return "FULL PAYMENT";
  if (cart?.paymentMethod === "pay_at_pickup") return "NO PAYMENT";
  if (cart?.delivery?.isPayOnline) return "FULL PAYMENT";

  const amountToCollect =
    Number(cart?.totalAmountToCollect || 0) -
    Number(cart?.totalResellerProfit || 0);
  const paymentCap = opsConfig?.pickUpPaymentThreshold || 0;

  const isFullPayment =
    cart?.paymentMethod === "pay_online" ||
    (cart?.delivery && amountToCollect < paymentCap);

  return isFullPayment ? "FULL PAYMENT" : "PART PAYMENT";
};

export const calculatePaymentType = (
  cart: ICart | null,
  hasAliExpressSupplier: boolean
): string => {
  if (hasAliExpressSupplier) return "PAY ONLINE";
  if (cart?.paymentMethod === "pay_online") return "PAY ONLINE";
  if (cart?.pickup) return "PAY AT PICKUP";
  if (cart?.delivery?.isPayOnline) return "PAY ONLINE";
  return "CASH ON DELIVERY";
};

// ============================================
// COUPON HELPERS
// ============================================

export const getRemainingFreeDeliveries = (coupon: any): number => {
  const totalDeliveries = coupon?.coupon?.free_deliveries ?? 0;
  const exhausted = coupon?.free_delivery_exhausted ?? 0;
  return Math.max(0, totalDeliveries - exhausted);
};

export const generateRandomString = (): string => {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";

  for (let i = 0; i < 13; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    result += characters.charAt(randomIndex);
  }
  return result;
};

